import { useMemo } from "react";
import type { ResourceMapEdge, ResourceMapNode, ResourceMapOmission, ResourceMapResponse } from "@k8s-dashboard/contracts";
import { StatusBadge } from "@/components/primitives";
import { VirtualTable } from "@/components/VirtualTable";
import { ResourceMapGraph, type ResourceMapGraphEdge, type ResourceMapGraphNode } from "./ResourceMapGraph";
import { RELATION_STATE_LABEL, StateBlock, TRUNCATION_REASON_LABEL, nodeLabel, scopeText } from "./ResourceMapNotices";

/**
 * Resource Map 성공 응답 패널 (ADR 0020, #8)
 * --------------------------------------------------------------------------
 * 서버 응답 값만 보여줍니다 — 요약·잘림·시각 미리보기(유계 부분집합)·관계 지원
 * 상태·생략 목록·**정본인 노드/간선 가상 스크롤 표**. 서버 배열 순서를 그대로
 * 보존하고 클라이언트 재정렬·레이아웃이 없습니다.
 */
export interface ResourceMapPanelsProps {
  data: ResourceMapResponse;
  requestedDepth: number;
  /** 재조회 실패 — 마지막 성공 그래프를 지우지 않고 고정 경고만 덧붙입니다. */
  refetchFailed: boolean;
  focusTitle: string;
  graphNodes: ReadonlyArray<ResourceMapGraphNode>;
  graphEdges: ReadonlyArray<ResourceMapGraphEdge>;
  onSelectNode: (node: ResourceMapGraphNode) => void;
}

export function ResourceMapPanels({
  data,
  requestedDepth,
  refetchFailed,
  focusTitle,
  graphNodes,
  graphEdges,
  onSelectNode,
}: ResourceMapPanelsProps) {
  /* 간선 라벨용 key→사람 라벨 표는 **한 번만** 만듭니다(노드 최대 500). 간선마다
     노드 배열을 훑으면 500×1500 작업이 됩니다. focus·미해석 노드도 응답 nodes에
     있으므로 이 표 하나로 전부 해석됩니다. */
  const labelByKey = useMemo(() => {
    const table = new Map<string, string>();
    for (const node of data.nodes) table.set(node.key, nodeLabel(node));
    return table;
  }, [data]);
  const endpointLabel = (key: string) => labelByKey.get(key) ?? key;

  return (
    <>
      {refetchFailed ? (
        /* 재조회 실패 — 마지막 성공 그래프를 지우지 않습니다. 낡았다는 사실은
           observedAt과 이 고정 경고가 전달합니다. */
        <StateBlock
          role="alert"
          notice={{
            title: "다시 조회가 실패했습니다 — 마지막 성공 그래프를 표시 중입니다",
            detail: "아래 그래프는 observedAt 시점의 것입니다. 잠시 후 다시 조회하세요.",
            tone: "degraded",
          }}
        />
      ) : null}
      {/* 신원·시각·Scope — 전부 서버 응답 값입니다. */}
      <dl className="chips" aria-label="그래프 요약">
        <div>
          <dt className="field__label">focus</dt>
          <dd>
            {nodeLabel(data.focus)} · UID <span className="num">{data.focus.uid || "-"}</span> ·{" "}
            {data.focus.resolved ? "해석됨" : "미해석"}
          </dd>
        </div>
        <div>
          <dt className="field__label">적용 Scope (서버)</dt>
          <dd>{scopeText(data.appliedScope)}</dd>
        </div>
        <div>
          <dt className="field__label">깊이</dt>
          <dd>
            요청 <span className="num">{requestedDepth}</span> · 적용 <span className="num">{data.depth}</span> / 최대{" "}
            <span className="num">{data.maxDepth}</span>
          </dd>
        </div>
        <div>
          <dt className="field__label">스냅숏 시각</dt>
          <dd>
            observedAt <span className="num">{data.observedAt}</span> · generatedAt{" "}
            <span className="num">{data.generatedAt}</span>
          </dd>
        </div>
        <div>
          <dt className="field__label">규모</dt>
          <dd>
            노드 <span className="num">{data.nodes.length.toLocaleString("ko-KR")}</span> · 간선{" "}
            <span className="num">{data.edges.length.toLocaleString("ko-KR")}</span>
          </dd>
        </div>
      </dl>

      {data.truncated ? (
        <StateBlock
          notice={{
            title: "그래프가 서버 상한에서 잘렸습니다",
            detail: `사유: ${data.truncation.reasons.map((r) => TRUNCATION_REASON_LABEL[r] ?? r).join(", ")} · 생략 노드 ${
              data.truncation.omittedNodes
            } · 생략 간선 ${data.truncation.omittedEdges}${
              data.truncation.lowerBound ? " (하한값 — 실제로는 더 많을 수 있습니다)" : ""
            }`,
            tone: "degraded",
          }}
        />
      ) : null}
      {data.truncated && Object.keys(data.truncation.byRelation).length > 0 ? (
        <p className="panel__subtitle">
          관계별 생략:{" "}
          {Object.entries(data.truncation.byRelation)
            .map(([relation, count]) => `${relation} ${count}`)
            .join(" · ")}
        </p>
      ) : null}

      {data.edges.length === 0 ? (
        <StateBlock
          role="status"
          notice={{
            title: "연결된 관계가 0건입니다",
            detail: "조회는 성공했습니다. 이 focus에서 현재 깊이 안의 관계가 없습니다.",
            tone: "neutral",
          }}
        />
      ) : null}

      {/* 시각 미리보기 — 접근성 표(아래 노드·간선 목록)를 대체하지 않는
          유계 부분집합입니다. 서버 순서 그대로, 힘-기반 레이아웃 없음. */}
      <ResourceMapGraph nodes={graphNodes} edges={graphEdges} onSelect={onSelectNode} title={focusTitle} />

      <h3 className="panel__title">관계 지원 상태</h3>
      <ul aria-label="관계 지원 상태">
        {data.relations.map((rel) => (
          <li key={rel.type}>
            <span className="num">{rel.type}</span> — {RELATION_STATE_LABEL[rel.state] ?? rel.state}
            {rel.reason ? ` · ${rel.reason}` : ""}
          </li>
        ))}
      </ul>

      {data.omissions.length > 0 ? (
        <>
          <h3 className="panel__title">
            색인이 없어 만들지 않은 관계 (총 <span className="num">{data.omissions.length.toLocaleString("ko-KR")}</span>건)
          </h3>
          <VirtualTable<ResourceMapOmission>
            items={data.omissions}
            columns={["34%", "16%", "50%"]}
            header={
              <tr>
                <th scope="col">노드</th>
                <th scope="col">관계</th>
                <th scope="col">사유</th>
              </tr>
            }
            getKey={(omission, index) => `${omission.node}|${omission.type}|${index}`}
            renderRow={(omission) => (
              <>
                <td>{endpointLabel(omission.node)}</td>
                <td>{omission.type}</td>
                <td>{omission.reason}</td>
              </>
            )}
          />
        </>
      ) : null}

      <h3 className="panel__title">노드</h3>
      <VirtualTable<ResourceMapNode>
        items={data.nodes}
        columns={["20%", "14%", "24%", "7%", "11%", "12%", "12%"]}
        header={
          <tr>
            <th scope="col">Kind</th>
            <th scope="col">Namespace</th>
            <th scope="col">이름</th>
            <th scope="col">깊이</th>
            <th scope="col">해석</th>
            <th scope="col">건강</th>
            <th scope="col">UID</th>
          </tr>
        }
        getKey={(node) => node.key}
        renderRow={(node) => (
          <>
            <td>
              {node.group === "core" ? "" : `${node.group}/`}
              {node.kind}
            </td>
            <td>{node.namespace ?? ""}</td>
            <td>{node.name}</td>
            <td className="num">{node.depth}</td>
            <td>{node.resolved ? "해석됨" : "미해석"}</td>
            <td>
              <StatusBadge severity={node.health} small />
            </td>
            <td className="num">{node.uid ?? ""}</td>
          </>
        )}
      />

      <h3 className="panel__title">간선</h3>
      <VirtualTable<ResourceMapEdge>
        items={data.edges}
        columns={["30%", "30%", "12%", "10%", "18%"]}
        header={
          <tr>
            <th scope="col">From</th>
            <th scope="col">To</th>
            <th scope="col">관계</th>
            <th scope="col">해석</th>
            <th scope="col">근거</th>
          </tr>
        }
        getKey={(edge, index) => `${edge.from}|${edge.to}|${edge.type}|${edge.evidence.kind}|${edge.evidence.field}|${index}`}
        renderRow={(edge) => (
          <>
            <td>{endpointLabel(edge.from)}</td>
            <td>{endpointLabel(edge.to)}</td>
            <td>{edge.type}</td>
            <td>{edge.resolved ? "해석됨" : "미해석"}</td>
            <td>
              {edge.evidence.kind} · {edge.evidence.field}
            </td>
          </>
        )}
      />
    </>
  );
}
